import { auth } from './firebase';

// AI calls go through our Cloud Function proxy (/api/ai) which holds the
// OpenRouter key server-side. The browser NEVER sees the key. The proxy
// requires a Firebase Auth ID token, so only logged-in QC Hub users can use it.
//
// Backend model: openrouter/free (auto-selects an available free model, incl.
// vision for image requests). Gemini was dropped — geo-blocked in Romania.

const PROXY_URL = 'https://qc-hub-ai-proxy.wisewomaniswise.workers.dev';

type ChatMessage =
  | { role: 'user' | 'system'; content: string }
  | {
      role: 'user';
      content: Array<
        | { type: 'text'; text: string }
        | { type: 'image_url'; image_url: { url: string } }
      >;
    };

async function callProxy(messages: ChatMessage[], maxTokens = 1500): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Please sign in to use AI features.');
  const idToken = await user.getIdToken();

  const res = await fetch(PROXY_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ messages, maxTokens })
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || `AI request failed (${res.status})`);
  }
  if (!data?.text) throw new Error('AI returned no text — try again.');
  return data.text as string;
}

export async function explainItem(opts: {
  itemDescription: string;
  acceptanceCriteria?: string | null;
  project: string;
}): Promise<string> {
  const prompt = `You are a friendly QC coach helping a brand-new frontend QC reviewer named Anca.
She has zero IT background and is doing visual / interactive QC on web dashboards.
She does NOT touch APIs, databases, or code. She only clicks, fills forms, looks at pages.

The dashboard she is reviewing right now is project: ${opts.project}.

The checklist item she needs to verify is:
"${opts.itemDescription}"
${opts.acceptanceCriteria ? `\nThe expected/acceptance criteria reads:\n"${opts.acceptanceCriteria}"` : ''}

Explain to her in plain, friendly English:
1) What this is actually asking her to do — step by step.
2) What it should look like when it works (a PASS).
3) What it would look like when it's broken (a FAIL) — what to watch for.
4) Any common edge cases she should also try (different browser widths, different user roles, hovering, refreshing, etc).

Keep it tight (under 200 words), use bullet points, no jargon. If a word IS jargon, define it inline.
Do NOT mention APIs, status codes, schemas, security rules, or anything backend-y.`;

  return callProxy([{ role: 'user', content: prompt }]);
}

export async function translateText(opts: {
  text: string;
  targetLang: 'ro' | 'en';
  context?: string;
}): Promise<string> {
  const langName = opts.targetLang === 'ro' ? 'Romanian' : 'English';
  const prompt = `Translate the following text to ${langName}. Keep ALL formatting (line breaks, bullet points, bold markers, emoji).
Use natural everyday ${langName} (not formal/academic). Do NOT translate brand names, proper nouns, dashboard names, or technical terms in code-style backticks.
${opts.context ? `Context (for tone): ${opts.context}\n` : ''}
Output ONLY the translation — no preamble, no quotes, no notes.

Text to translate:
${opts.text}`;
  return callProxy([{ role: 'user', content: prompt }]);
}

export async function suggestNotesFromScreenshot(opts: {
  imageBase64: string;
  imageMimeType: string;
  itemDescription: string;
  acceptanceCriteria?: string | null;
  project: string;
}): Promise<string> {
  const prompt = `You are a QC coach helping Anca, a brand-new frontend QC reviewer (no IT background).
She just uploaded a screenshot of something she saw during QC on the ${opts.project} dashboard.

The checklist item she is verifying:
"${opts.itemDescription}"
${opts.acceptanceCriteria ? `Expected: "${opts.acceptanceCriteria}"` : ''}

Look at the screenshot. Draft QC notes she can edit and save. The notes should:
- Describe in plain English what you see vs what should be there
- Be specific (mention colors, positions, alignment, missing elements, etc)
- Use her voice (1st person: "I noticed that...", "I see...")
- Be 2-4 short sentences
- Avoid backend jargon (no "API", "status code", "schema")

Just output the notes text — no preamble like "Here are the notes:" — straight to the content.`;

  const dataUrl = `data:${opts.imageMimeType};base64,${opts.imageBase64}`;
  return callProxy([
    {
      role: 'user',
      content: [
        { type: 'text', text: prompt },
        { type: 'image_url', image_url: { url: dataUrl } }
      ]
    }
  ]);
}
